import { useState, useEffect } from "react";
import { RefreshCw } from "lucide-react";

/**
 * Prosta weryfikacja że użytkownik nie jest botem:
 * - honeypot: ukryte pole, które boty wypełniają
 * - czas: minimum 2s między otwarciem a wysłaniem
 * - zagadka matematyczna: prosta operacja do rozwiązania
 *
 * Zwraca:
 *   - values: obiekt do wysłania w formularzu ({hp, ts, math_a, math_b, math_answer})
 *   - isValid(): true jeśli odpowiedź poprawna i nie honeypot
 *   - error: komunikat jeśli błąd
 *   - Component: element do wyrenderowania w formularzu
 */
export function useHumanCheck() {
  const [openedAt] = useState(() => Date.now());
  const [hp, setHp] = useState("");
  const [math, setMath] = useState(() => generateMath());
  const [answer, setAnswer] = useState("");
  const [error, setError] = useState(null);

  function generateMath() {
    const a = Math.floor(Math.random() * 9) + 1;
    const b = Math.floor(Math.random() * 9) + 1;
    return { a, b, correct: a + b };
  }

  const regenerate = () => {
    setMath(generateMath());
    setAnswer("");
    setError(null);
  };

  const isValid = () => {
    setError(null);
    if (hp) {
      setError("Wykryto błąd. Odśwież stronę.");
      return false;
    }
    if (Date.now() - openedAt < 2000) {
      setError("Formularz wysłany za szybko. Spróbuj ponownie.");
      return false;
    }
    if (parseInt(answer, 10) !== math.correct) {
      setError("Nieprawidłowa odpowiedź na pytanie zabezpieczające.");
      regenerate();
      return false;
    }
    return true;
  };

  const values = {
    hp,
    ts: openedAt,
    math_a: math.a,
    math_b: math.b,
    math_answer: parseInt(answer, 10) || 0,
  };

  const element = (
    <div className="sm:col-span-2 bg-slate-50 border border-slate-200 rounded-lg p-4">
      {/* Honeypot – niewidoczny dla ludzi, boty go wypełnią */}
      <input
        type="text"
        name="website"
        value={hp}
        onChange={(e) => setHp(e.target.value)}
        tabIndex={-1}
        autoComplete="off"
        style={{ position: "absolute", left: "-9999px", opacity: 0, pointerEvents: "none" }}
        aria-hidden="true"
      />

      <label className="text-xs font-bold text-slate-700 uppercase tracking-wider block">
        Potwierdź, że nie jesteś botem *
      </label>
      <div className="flex items-center gap-3 mt-2 flex-wrap">
        <div className="font-mono text-lg text-slate-800 select-none bg-white border border-slate-300 rounded-md px-4 py-2">
          {math.a} + {math.b} = ?
        </div>
        <input
          data-testid="human-answer"
          type="number"
          value={answer}
          onChange={(e) => { setAnswer(e.target.value); if (error) setError(null); }}
          className={`rm-input !w-24 !mt-0 ${error ? "rm-input-error" : ""}`}
          placeholder="?"
        />
        <button
          type="button"
          onClick={regenerate}
          className="text-slate-500 hover:text-[#1f3a8a] p-2"
          title="Nowe pytanie"
          data-testid="human-refresh"
        >
          <RefreshCw size={16} />
        </button>
      </div>
      {error && (
        <div className="rm-error-text mt-2" data-testid="human-error">{error}</div>
      )}
    </div>
  );

  return { values, isValid, element, error };
}
